import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { ProjectService } from '../../../../../../common/services/project.service';
import { SearchService } from '../../../../../../common/services/search.service';
import { CustomizerSettingsService } from '../../../../../../customizer-settings/customizer-settings.service';
import { ContractSummary, ProjectType } from '../../../../../../common/models/project';
import { GenericDialogComponent } from '../../../../../../common/components/dialog/dialog';
import { HttpParams } from '@angular/common/http';
import { Subject, takeUntil } from 'rxjs';


@Component({
    selector: 'app-contracts',
    templateUrl: './contracts.component.html',
    styleUrl: './contracts.component.scss',
    imports: [MatCardModule,
        MatMenuModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatTooltipModule,
        MatIconModule,
        MatDialogModule,
        MatSnackBarModule,
        CommonModule
    ]
})
export class ContractsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    projectId: string = '';

    projectType: ProjectType = ProjectType.Application;

    contracts: ContractSummary[] = [];
    filteredContracts: ContractSummary[] = [];

    isToggled = false;

    public ProjectType = ProjectType;

    private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    };
  
    constructor(
      private router: Router,
      private projectService: ProjectService,
      private searchService: SearchService,
      public themeService: CustomizerSettingsService,
      private dialog: MatDialog,
      private snackBar: MatSnackBar,
      private route: ActivatedRoute
    ) {}
  
    ngOnInit() {
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });
      this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
        this.applySearch(term);
      });

      this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
          this.projectId = params.get('projectId') ?? '';
          this.fetchContracts();
      });
      
      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras?.state || history.state;
      if (state && state.projectType !== undefined) {
        this.projectType = state.projectType as ProjectType;
      }
  
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

  
    fetchContracts(): void {
      this.projectService.getContracts(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
        next: (data) => {
          this.contracts = data;
          this.filteredContracts = data;
        },
        error: (error) => {
          console.error('Error fetching contracts', error);
        }
      });
    }

    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredContracts = this.contracts.filter((contract: ContractSummary) => {
          return (
             contract.classification.toLowerCase().includes(searchTerm) ||
             contract.refNumber.toLowerCase().includes(searchTerm) ||
             contract.number.toLowerCase().includes(searchTerm)
          );
        });
      } else {
        this.filteredContracts = [...this.contracts];
      }
    }

    confirmDelete(contract: ContractSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite brisanje',
          message: `Jeste li sigurni da želite obrisati ugovor?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().subscribe((result) => {
        if (result === true) {
          this.deleteContract(contract.id);
        }
      });
    }
  
    deleteContract(contractId: string): void {
      this.projectService.deleteContract(this.projectId, contractId).pipe(takeUntil(this.destroy$)).subscribe(
        () => {
          this.fetchContracts();
          this.snackBar.open('Ugovor je obrisan', 'Zatvori', this.snackbarConfig);
        },
        (error) => {
          console.error('Error deleting contract', error);
        }
      );
    }

    downloadContract(contract: ContractSummary): void {
      this.projectService.downloadContract(this.projectId, contract.id).pipe(takeUntil(this.destroy$)).subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'B7 Obrazac ugovora o financiranju.docx';
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: (err) => {
          console.error('Error downloading contract', err);
        }
      });
    }

    goToCreateContract(): void {
      this.router.navigate(['municipality', 'projects', this.projectId, 'contracts', 'create'], {
        state: { projectType: this.projectType }
      });
    }
  
    goToEditContract(contract: ContractSummary): void {
      this.router.navigate(['municipality', 'projects', this.projectId, 'contracts', contract.id, 'edit'], {
        state: { projectType: this.projectType }
      });
    }
  }
